import React, { useState } from 'react';
import moment from 'moment/moment';
import lodashCompact from 'lodash/compact';

import { formatPhoneNumber } from 'common/formatters';

import {
  UTILITY_USER_ROLE_ADMIN,
  UTILITY_USER_ROLE_PARTNER_ADMIN,
  UTILITY_USER_ROLE_PARTNER_ACCOUNTING,
  UTILITY_USER_ROLE_PARTNER_PROPERTY_MANAGER,
  UTILITY_USER_ROLE_VIEW_REVENUE,
} from 'common/utility/userRoles';
import { DATA_TABLE_SOURCE_UTILITY_PARTNER_USERS } from 'common/dataTableConstants';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import {
  SR_API_ACTION_UTILITY_PARTNER_UPSERT_USER,
  SR_API_ACTION_UTILITY_PARTNER_DELETE_USER,
  SR_API_ACTION_UTILITY_PARTNER_SEND_INVITE,
} from 'api/utilityActionTypes';

import setNullEmptyValues from 'common/util/setNullEmptyValues';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrUtilityProfitPartnerUsersDataTableHeader from 'client/components/SrUtilityProfitPartnerUsersDataTable/SrUtilityProfitPartnerUsersDataTableHeader';
import SrUtilityProfitPartnerUpsertPartnerUserModal from 'client/components/SrUtilityProfitPartnerUsersDataTable/SrUtilityProfitPartnerUpsertPartnerUserModal';
import SrUtilityProfitPartnerUserConfirmationModal from 'client/components/SrUtilityProfitPartnerUsersDataTable/SrUtilityProfitPartnerUserConfirmationModal';
import SrUtilityProfitPartnerUserGroupModal from 'client/components/SrUtilityProfitPartnerUsersDataTable/SrUtilityProfitPartnerUserGroupModal';

const getUserRolesToDisplay = ({ utilityPartner }) => {
  return lodashCompact([
    {
      role: UTILITY_USER_ROLE_PARTNER_ADMIN,
      label: 'Admin (add/delete users)',
    },
    utilityPartner.experienceConfiguration.detailedModeActive && {
      role: UTILITY_USER_ROLE_PARTNER_ACCOUNTING,
      label: 'Transfer Money',
    },
    {
      role: UTILITY_USER_ROLE_PARTNER_PROPERTY_MANAGER,
      label: 'Switch Platforms',
    },
    utilityPartner.experienceConfiguration.detailedModeActive && {
      role: UTILITY_USER_ROLE_VIEW_REVENUE,
      label: 'View Revenue',
    },
  ]);
};

const DATA_TABLE_HEADERS = [
  {
    content: 'Name',
    style: { width: '300px' },
  },
  {
    content: 'Email',
    dataTableFieldId: 'email',
    sortPosition: 'right',
  },
  {
    content: 'Phone',
    dataTableFieldId: 'phone',
    sortPosition: 'right',
  },
  {
    content: 'Permissions',
  },
  {
    content: 'Last Active',
    dataTableFieldId: 'lastVisitedAt',
    sortPosition: 'right',
  },
  {},
];

function getDataTableSettingsFromProps({ utilityPartner }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_UTILITY_PARTNER_USERS,
    rootViewId: DATA_TABLE_SOURCE_UTILITY_PARTNER_USERS,
    initialViewSettings: {
      filter: {
        ...(utilityPartner ? { 'utilityUserPartners.utilityPartnerId': utilityPartner.id } : {}),
      },
      sort: {
        firstName: 'asc',
        lastVisitedAt: 'desc',
      },
      loadAllData: true,
    },
    itemsPropName: 'partnerUsers',
    loadAllData: true,
  };
  return dataTableSettings;
}

const SrUtilityProfitPartnerUsersDataTable = ({
  utilityPartner,
  utilityUser,
  utilityPartnerList,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [teamMemberModalArgs, setTeamMemberModalArgs] = useState(null);
  const [teamMemberModalError, setTeamMemberModalError] = useState(null);
  const [teamMemberPartnerListModalArgs, setTeamMemberPartnerListModalArgs] = useState(null);
  const [successModalArgs, setSuccessModalArgs] = useState(null);
  const canAddOrEditTeamMembers =
    utilityUser.roles.includes(UTILITY_USER_ROLE_ADMIN) ||
    utilityUser.roles.includes(UTILITY_USER_ROLE_PARTNER_ADMIN);
  const userRolesToDisplay = getUserRolesToDisplay({ utilityPartner });

  const dispatch = useDispatch();

  const getUtilityUserName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }

    return 'Invited User';
  };

  const handleTeamMemberSave = ({ formValues, reloadItems }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_UPSERT_USER,
      payload: {
        utilityPartnerId: utilityPartner.id,
        user: setNullEmptyValues(formValues),
        roles: formValues.roles,
      },
      onSuccess: ({ user }) => {
        setTeamMemberModalArgs(null);
        setTeamMemberModalError(null);

        if (!formValues.utilityUserId) {
          setSuccessModalArgs({
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            onClose: () => {
              setSuccessModalArgs(null);
            },
          });
        }

        return reloadItems({
          count: 1,
          filter: { email: user.email },
        });
      },
      onError: (error) => {
        setTeamMemberModalError(error);
      },
    });
  };

  const handleResendInvite = ({ user }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_SEND_INVITE,
      payload: { utilityPartnerId: utilityPartner.id, user },
      onSuccess: () => {
        setTeamMemberModalArgs(null);
        setTeamMemberModalError(null);

        setSuccessModalArgs({
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          onClose: () => {
            setSuccessModalArgs(null);
          },
        });
      },
    });
  };

  const onOpenUpsertModal = ({ user, reloadItems }) => {
    setTeamMemberModalArgs({
      user,
      reloadItems,
      onResendInvite: () => {
        handleResendInvite({ user });
      },
      onSave: (formValues) => {
        handleTeamMemberSave({ formValues, reloadItems });
      },
      onCancel: () => {
        setTeamMemberModalArgs(null);
        setTeamMemberModalError(null);
      },
    });
  };

  const onSearchInputChange = ({ viewSettings, input }) => {
    const searchTerm = input.target.value;
    setSearchInput(searchTerm);
    if (viewSettings) {
      viewSettings.update({
        ...viewSettings,
        loadAllData: true,
        fuzzy: !searchTerm
          ? null
          : {
            queryString: searchTerm,
            columns: ['firstName', 'lastName', 'phone', 'email'],
          },
      });
    }
  };

  const onDeleteUser = ({ user }) => {
    if (
      !global.confirm(`Are you sure you want to delete the user "${getUtilityUserName(user)}"?`)
    ) {
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_DELETE_USER,
      payload: {
        partnerId: utilityPartner.id,
        userId: user.id,
      },
      onSuccess: () => {
        setTeamMemberModalArgs(null);
        setTeamMemberModalError(null);

        dispatch(({ state }) => {
          const dataSource = DATA_TABLE_SOURCE_UTILITY_PARTNER_USERS;
          const statePath = ['dataTables', dataSource, 'items'];
          state.set(
            statePath,
            state.get(statePath).filter((item) => item.id !== user.id),
          );
        });
      },
    });
  };

  return (
    <>
      <SrDataTableConnected {...getDataTableSettingsFromProps({ utilityPartner })}>
        {({
          partnerUsers,
          reloadItems,
          viewSettings,
          dataTableApiState,
          dataTableIsFullyLoaded,
        }) => {
          // eslint-disable-line max-len
          if (dataTableApiState.inProgress && partnerUsers.length === 0) {
            return (
              <div>
                <SrBox width="100%">
                  <SrUtilityProfitPartnerUsersDataTableHeader
                    onSearchInputChange={(input) =>
                      onSearchInputChange({
                        viewSettings,
                        input,
                      })
                    }
                    onAddMemberClick={() => onOpenUpsertModal({ reloadItems })}
                    searchInput={searchInput}
                  />
                </SrBox>
                <SrBox marginTop="20px" paddingBottom="56px">
                  <div>
                    <div className="loading-ellipses clearfix" />
                  </div>
                </SrBox>
              </div>
            );
          }

          const partnerUserRow = partnerUsers.map((partnerUser) => {
            const roles = userRolesToDisplay.filter((role) =>
              partnerUser.roles.includes(role.role),
            );
            const canEdit = partnerUser.id === utilityUser.id || canAddOrEditTeamMembers;

            if (!roles.length) {
              roles.push({
                role: 'No permissions',
                label: 'No permissions',
              });
            }

            return {
              cells: [
                {
                  content: getUtilityUserName(partnerUser),
                },
                {
                  content: partnerUser.email,
                  style: { fontWeight: '500' },
                },
                {
                  content: formatPhoneNumber(partnerUser.phone),
                  style: { fontWeight: '500' },
                },
                {
                  content: (
                    <>
                      {roles.map((role) => (
                        <SrText
                          key={role.role}
                          font="greycliff"
                          weight="400"
                          fontSize="14px"
                          color="blue"
                        >
                          {role.label}
                          <br />
                        </SrText>
                      ))}
                    </>
                  ),
                  style: { fontWeight: '400' },
                },
                {
                  content: partnerUser.lastVisitedAt
                    ? moment(partnerUser.lastVisitedAt).format('MMM D, YYYY')
                    : 'Never logged in',
                  style: { color: partnerUser.lastVisitedAt ? '#132630' : '#E7711B' },
                },
                {
                  content: canEdit ? (
                    <SrText
                      font="greycliff"
                      weight="700"
                      fontSize="16px"
                      color="blue300"
                      cursor="pointer"
                      onClick={() => onOpenUpsertModal({ user: partnerUser, reloadItems })}
                    >
                      Edit
                    </SrText>
                  ) : null,
                },
              ],
            };
          });

          return (
            <div>
              <SrBox width="100%">
                <SrUtilityProfitPartnerUsersDataTableHeader
                  onSearchInputChange={(input) =>
                    onSearchInputChange({
                      viewSettings,
                      input,
                    })
                  }
                  onAddMemberClick={() => onOpenUpsertModal({ reloadItems })}
                  searchInput={searchInput}
                  canAddOrEditTeamMembers={canAddOrEditTeamMembers}
                />
              </SrBox>
              <SrBox marginTop="20px" paddingBottom="56px">
                <div>
                  <SrDataTableUI
                    sticky
                    theme={SR_TABLE_THEME_PARTNER_CLEAR}
                    dataTableViewSettings={viewSettings}
                    headers={DATA_TABLE_HEADERS}
                    minWidth="100%"
                    rows={partnerUserRow}
                  />
                  {dataTableApiState.inProgress && (
                    <div>
                      <div className="loading-ellipses clearfix" />
                    </div>
                  )}
                </div>
                {partnerUsers.length === 0 && dataTableIsFullyLoaded && (
                  <SrBox
                    backgroundColor="white"
                    display="flex"
                    justifyContent="center"
                    padding="20px"
                  >
                    <SrText font="greycliff" weight="500" fontSize="16px" fontStyle="italic">
                      {searchInput ? 'No search results.' : 'No team members found.'}
                    </SrText>
                  </SrBox>
                )}
              </SrBox>
            </div>
          );
        }}
      </SrDataTableConnected>

      {teamMemberModalArgs && (
        <SrUtilityProfitPartnerUpsertPartnerUserModal
          {...teamMemberModalArgs}
          utilityPartnerList={utilityPartnerList}
          currentUtilityUser={utilityUser}
          utilityPartner={utilityPartner}
          canAddOrEditTeamMembers={canAddOrEditTeamMembers}
          onDeleteUser={onDeleteUser}
          onUpdateUtilityPartnerIds={({ utilityPartnerIds, onUpdateUtilityPartnerIds }) => {
            setTeamMemberPartnerListModalArgs({
              utilityPartnerIds,
              onClose: () => {
                setTeamMemberPartnerListModalArgs(null);
              },
              onSubmit: onUpdateUtilityPartnerIds,
            });
          }}
          error={teamMemberModalError}
        />
      )}
      {successModalArgs && <SrUtilityProfitPartnerUserConfirmationModal {...successModalArgs} />}

      {teamMemberPartnerListModalArgs && (
        <SrUtilityProfitPartnerUserGroupModal
          {...teamMemberPartnerListModalArgs}
          utilityPartnerList={utilityPartnerList}
          onClose={() => {
            setTeamMemberPartnerListModalArgs(null);
          }}
        />
      )}
    </>
  );
};

export default SrUtilityProfitPartnerUsersDataTable;
